import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, LogIn, UserPlus, GraduationCap } from "lucide-react";
import { ASSETS, SCHOOL_INFO } from "@/lib/assets";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";

const links = [
  { to: "/", label: "Accueil" },
  { to: "/a-propos", label: "À propos" },
  { to: "/cycles", label: "Cycles" },
  { to: "/admissions", label: "Admissions" },
  { to: "/galerie", label: "Galerie" },
  { to: "/tableau-honneur", label: "Tableau d'honneur" },
  { to: "/actualites", label: "Actualités" },
  { to: "/recrutement", label: "Rejoignez-nous" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <header
      className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-200"
      data-testid="public-navbar"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
          <img
            src={ASSETS.logo}
            alt="Logo Les Écoles Mazid"
            className="h-14 w-14 rounded-xl object-contain bg-white ring-1 ring-slate-200 p-0.5"
          />
          <div className="leading-tight">
            <div className="font-heading font-extrabold text-[#1E3A8A] text-lg tracking-tight">
              LES ÉCOLES MAZID
            </div>
            <div className="text-[10px] tracking-[0.25em] text-[#EA580C] font-semibold uppercase">
              {SCHOOL_INFO.tagline}
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-").replace(/[éà]/g, "e")}`}
              className={({ isActive }) =>
                `px-3.5 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive
                    ? "text-[#166534] bg-green-50"
                    : "text-slate-700 hover:text-[#166534] hover:bg-slate-50"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          {user ? (
            <Button
              onClick={() => navigate("/dashboard")}
              className="bg-[#166534] hover:bg-[#14532d] text-white rounded-md gap-2"
              data-testid="nav-dashboard-btn"
            >
              <GraduationCap className="h-4 w-4" />
              Mon espace
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => navigate("/connexion")}
                className="border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white rounded-md gap-2"
                data-testid="nav-login-btn"
              >
                <LogIn className="h-4 w-4" />
                Connexion
              </Button>
              <Button
                onClick={() => navigate("/inscription")}
                className="bg-[#EA580C] hover:bg-orange-700 text-white rounded-md gap-2"
                data-testid="nav-register-btn"
              >
                <UserPlus className="h-4 w-4" />
                Inscrire un enfant
              </Button>
            </>
          )}
        </div>

        <button
          className="lg:hidden p-2 rounded-md border border-slate-200"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white px-5 py-4 space-y-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `block px-3 py-2 rounded-md text-sm font-medium ${
                  isActive ? "bg-green-50 text-[#166534]" : "text-slate-700"
                }`
              }
              data-testid={`mobile-nav-${l.label.toLowerCase()}`}
            >
              {l.label}
            </NavLink>
          ))}
          <div className="pt-3 flex flex-col gap-2 border-t border-slate-100">
            {user ? (
              <Button onClick={() => { setOpen(false); navigate("/dashboard"); }} className="bg-[#166534] text-white">
                Mon espace
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => { setOpen(false); navigate("/connexion"); }} className="border-[#1E3A8A] text-[#1E3A8A]">
                  Connexion
                </Button>
                <Button onClick={() => { setOpen(false); navigate("/inscription"); }} className="bg-[#EA580C] text-white">
                  Inscrire un enfant
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
